import React, { useState } from "react";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Alert from "react-bootstrap/Alert";
import { useAuth } from "../../contexts/AuthContext";
import { portalLogin, portalForgotPassword } from "../../services/authService";
import InputField from "../../utils/InputField";
import { useToast } from "../../contexts/ToastContext";
import { useLoading } from "../../contexts/LoadingContext";

function PortalLogin() {
  const { login } = useAuth();
  const { showToast } = useToast();
  const { startLoading, stopLoading } = useLoading();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");

  const handleFieldChange = (field, value) => {
    if (field === "username") setUsername(value);
    if (field === "password") setPassword(value);
    if (field === "forgotEmail") setForgotEmail(value);
  };

  const validateForm = () => {
    const errors = {};
    if (!username) errors.username = "Email is required";
    if (!password) errors.password = "Password is required";
    return errors;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setServerError("");

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      startLoading();
      const data = await portalLogin(username, password);
      login(data.accessToken, "applicant");
      showToast("Login successful!", "success");
    } catch (error) {
      showToast(error.message || "Login failed. Please try again.", "error");
      setServerError(error.message || "Login failed. Please try again.");
    } finally {
      stopLoading();
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      showToast("Please enter your email address.", "error");
      return;
    }
    try {
      startLoading();
      const data = await portalForgotPassword(forgotEmail);
      setForgotMessage(data.message);
      showToast(data.message, "success");
    } catch (error) {
      showToast(error.message || "Failed to send reset link.", "error");
    } finally {
      stopLoading();
    }
  };

  if (showForgotPassword) {
    return (
      <section>
        <Container
          className="d-flex align-items-center justify-content-center login-background"
          fluid
        >
          <div className="login-modal">
            <div className="d-flex justify-content-center">
              <img src="/logo.png" alt="Logo" className="login-logo" />
            </div>
            <h5 className="text-center mb-3" style={{ color: "#275317" }}>
              Reset Your Password
            </h5>
            <p className="text-center text-muted mb-4" style={{ fontSize: "14px" }}>
              Enter your email address and we'll send you a link to reset your password.
            </p>
            {forgotMessage && <Alert variant="success">{forgotMessage}</Alert>}
            <Form onSubmit={handleForgotPassword}>
              <Row className="px-0 mb-3">
                <InputField
                  field="forgotEmail"
                  label="Email Address"
                  type="email"
                  value={forgotEmail}
                  onChange={handleFieldChange}
                  as={Col}
                  controlId="portalForgotEmail"
                />
              </Row>
              <Button
                className="login-button mt-3"
                id="portalForgotButton"
                size="xl"
                type="submit"
              >
                Send Reset Link
              </Button>
              <div className="text-center mt-3">
                <Button
                  variant="link"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotMessage("");
                  }}
                  style={{ color: "#275317", textDecoration: "none", fontSize: "14px" }}
                >
                  ← Back to Login
                </Button>
              </div>
            </Form>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <section>
      <Container
        className="d-flex align-items-center justify-content-center login-background"
        fluid
      >
        <div className="login-modal">
          <div className="d-flex justify-content-center">
            <img src="/logo.png" alt="Logo" className="login-logo" />
          </div>
          <h5 className="text-center mb-1" style={{ color: "#275317", fontWeight: "600" }}>
            Applicant Portal
          </h5>
          <p className="text-center text-muted mb-4" style={{ fontSize: "14px" }}>
            Log in to view and manage your permit applications
          </p>
          {serverError && <Alert variant="danger">{serverError}</Alert>}
          <Form onSubmit={handleLogin}>
            <Row className="px-0 mb-3">
              <InputField
                field="username"
                label="Email"
                type="email"
                value={username}
                onChange={handleFieldChange}
                error={validationErrors.username}
                as={Col}
                controlId="portalLoginEmail"
              />
            </Row>
            <Row className="px-0 mb-3">
              <InputField
                field="password"
                label="Password"
                type="password"
                value={password}
                onChange={handleFieldChange}
                error={validationErrors.password}
                as={Col}
                controlId="portalLoginPassword"
              />
            </Row>
            <Button
              className="login-button mt-3"
              id="portalLoginButton"
              size="xl"
              type="submit"
            >
              Log In
            </Button>
            <div className="text-center mt-3">
              <Button
                variant="link"
                onClick={() => setShowForgotPassword(true)}
                style={{ color: "#275317", textDecoration: "none", fontSize: "14px" }}
              >
                Forgot Password?
              </Button>
            </div>
          </Form>
        </div>
      </Container>
    </section>
  );
}

export default PortalLogin;
